import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import {
  buildAttributePayloadFilterItem,
  buildAttributeValueFilterItem,
  buildElementTypeFilterItem,
  buildRelationObjectFilterItem,
  buildRelationObjectTypeFilterItem,
  buildRelationTargetFilterItem,
  buildScopeFilterItem,
  buildSystemAttributeFilterItem,
  resolveFilterValueEditor,
  type FilterChipEditContext,
  type FilterItem,
  type FilterItemLabel,
  type ObjectSearchRequest,
  type ObjectSearchSuggestion,
} from '@bizzdesign/data-access-search';
import type {
  AttributeCategoryDefinitionDto,
  AttributeDataPayload,
  BDTreeNode,
} from '@bizzdesign/shared/models';
import { ValueEditorComponent } from '../value-editor/value-editor.component';

/**
 * Inline editor for a single applied filter's **value**. Resolves the control a
 * {@link FilterItem} needs, hands it to {@link ValueEditorComponent}, and maps the
 * raw selection that comes back onto an updated filter emitted via {@link updateFilter}.
 *
 * @remarks Presentational only — it never mutates a store. The value-to-`FilterItem`
 * mapping lives in pure `build*FilterItem` utilities; this component only routes the
 * editor's events to them. Consumed by {@link InteractiveFilterChipComponent}.
 */
@Component({
  selector: 'bd-interactive-filter-value',
  imports: [ValueEditorComponent],
  templateUrl: './interactive-filter-value.component.html',
  styleUrl: './interactive-filter-value.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractiveFilterValueComponent {
  filter = input.required<FilterItem>();
  /** The trailing label segment rendered on the link-styled value trigger, supplied by the chip. */
  valueSegment = input<FilterItemLabel>();
  attributeCategoryDefinitions = input<AttributeCategoryDefinitionDto[]>([]);
  editContext = input<FilterChipEditContext>();
  disabled = input<boolean>(false);

  updateFilter = output<FilterItem>();
  searchObjects = output<ObjectSearchRequest>();
  searchUsers = output<string>();

  readonly editor = computed(() =>
    resolveFilterValueEditor(
      this.filter(),
      this.attributeCategoryDefinitions(),
      this.editContext(),
    ),
  );

  /** Only the user dropdown loads its options server-side. */
  readonly optionsLoading = computed<boolean>(() =>
    this.isUserDropdown() ? (this.editContext()?.usersLoading ?? false) : false,
  );

  /**
   * Every dropdown with enough options renders a filter field, but only the user
   * dropdown filters server-side; the rest filter their own options locally.
   */
  onFilterOptions(term: string): void {
    if (this.isUserDropdown()) {
      this.searchUsers.emit(term);
    }
  }

  private isUserDropdown(): boolean {
    const editor = this.editor();
    return editor?.kind === 'dropdown' && editor.dropdownKind === 'user';
  }

  onDropdownChange(node: BDTreeNode | undefined): void {
    const editor = this.editor();
    if (!node || editor?.kind !== 'dropdown') {
      return;
    }
    const filter = this.filter();
    switch (editor.dropdownKind) {
      case 'enum':
      case 'boolean':
        this.emitAttributeValue(node, editor.dropdownKind);
        return;
      case 'scope': {
        if (!node.data) {
          return;
        }
        const updatedFilter = buildScopeFilterItem(filter, node.data);
        if (updatedFilter) {
          this.updateFilter.emit(updatedFilter);
        }
        return;
      }
      case 'elementType': {
        if (!node.data) {
          return;
        }
        this.updateFilter.emit(buildElementTypeFilterItem(filter, node.data));
        return;
      }
      case 'relationObjectType': {
        const relationFilter = filter.value.relationFilters[0];
        if (!node.data || !relationFilter) {
          return;
        }
        this.updateFilter.emit(
          buildRelationObjectTypeFilterItem(filter, relationFilter, node.data),
        );
        return;
      }
      case 'relationTarget': {
        const relationFilter = filter.value.relationFilters[0];
        if (!node.data || !relationFilter) {
          return;
        }
        this.updateFilter.emit(
          buildRelationTargetFilterItem(filter, relationFilter, node.data),
        );
        return;
      }
      case 'user': {
        const user = node.systemAttributesUser;
        if (!user) {
          return;
        }
        this.updateFilter.emit(
          buildSystemAttributeFilterItem(
            filter,
            user,
            this.editContext()?.authenticatedUser,
          ),
        );
        return;
      }
    }
  }

  private emitAttributeValue(
    node: BDTreeNode,
    dropdownKind: 'enum' | 'boolean',
  ): void {
    const filter = this.filter();
    const attributeFilter = filter.value.attributeFilters[0];
    if (!attributeFilter || node.key === undefined) {
      return;
    }
    this.updateFilter.emit(
      buildAttributeValueFilterItem(
        filter,
        attributeFilter,
        dropdownKind === 'boolean' ? node.key === 'true' : node.key,
        this.attributeCategoryDefinitions(),
      ),
    );
  }

  onAttributeValueChange(payload: AttributeDataPayload): void {
    const attributeFilter = this.filter().value.attributeFilters[0];
    // Clearing the field emits an empty payload; committing it would leave a filter with
    // no value, which stops being editable and matches nothing.
    const hasValue = payload.value !== null && payload.value !== undefined;
    if (!attributeFilter || !hasValue) {
      return;
    }
    this.updateFilter.emit(
      buildAttributePayloadFilterItem(
        this.filter(),
        attributeFilter,
        payload,
        this.attributeCategoryDefinitions(),
      ),
    );
  }

  onRelationObjectChange(object: ObjectSearchSuggestion): void {
    const relationFilter = this.filter().value.relationFilters[0];
    // Re-picking the object the filter already targets must not re-run the search.
    const isAppliedObject =
      relationFilter?.relation.relatedElement?.id?.[0] === object.id;
    if (!relationFilter || isAppliedObject) {
      return;
    }
    this.updateFilter.emit(
      buildRelationObjectFilterItem(this.filter(), relationFilter, object),
    );
  }
}
